import {CreateAccessor} from "mobx-graphlink";
import moment from "moment";
import {GetSession} from "../../../Server/DB/sessions.js";
import {GetSessionEEGData, GetSessionGeneralHistory, GetSessionGyroSampleFiles} from "../../../Utils/Bridge/Bridge_Preload.js";
import {SamplesBySecond} from "../../../Server/DB/testSegments/@TestSegment.js";
import {GDBelt_RawDataChunk, gdBelt_rawDataDB} from "../../../Utils/IndexedDB/GDBelt_RawDataDB.js";
import {store} from "../../index.js";
import {IsSessionUIOpen, SessionUITab} from "../timeline.js";
import {EngineConfig} from "../../../Server/DB/engineConfigs/@EngineConfig.js";
import {E} from "js-vextensions";
import {GetSessionProcessors} from "../../../UI/@Shared/Processors/@ProcessorInstances.js";

let GetSelectedSessionExternals_lastResult: SessionExternals|n;
export const GetSelectedSessionExternals = CreateAccessor(()=>{
	const uiState = store.main.timeline.sessions;
	const sessionID = uiState.selectedSession;
	if (sessionID == null) return null;
	// if selected-session derivative-data isn't loaded yet, delay the loading till on a page that actually uses it
	if (GetSelectedSessionExternals_lastResult == null && !IsSessionUIOpen(SessionUITab.details)) {
		return GetSelectedSessionExternals_lastResult;
	}

	const result = GetSessionDerivativeData(sessionID);
	GetSelectedSessionExternals_lastResult = result;
	return result;
});
export const GetSelectedSessionExternals_AllowingResim = CreateAccessor((): SessionExternals_AllowingResim|n=>{
	const uiState = store.main.timeline.sessions;
	const sessionID = uiState.selectedSession;
	const session = GetSession(sessionID);
	if (session == null) return null;

	const realData = E(GetSelectedSessionExternals(), {config: session.config});
	if (store.main.timeline.sessions.processing_useCurrentConfig) {
		return E(realData, GetSessionProcessors().resim?.GetSessionDataOverrides(realData));
	}
	return realData;
});

export type GeneralHistory = {
	eegActivity: {activityByTime: {[key: string]: number}};
	gyroMotion: {motionsByTime: {[key: string]: boolean}};
	camFilenames: string[];
};
export type GyroSampleFile = {
	samplesBySecond: SamplesBySecond;
};
export type EEGSampleFile = {
	samplesBySecond: SamplesBySecond;
};

export type SessionExternals_AllowingResim = SessionExternals & {config: EngineConfig};
export class SessionExternals {
	constructor(data: SessionExternals) {
		Object.assign(this, data);
	}

	sessionID: string;

	// from multiple sources
	generalHistory: GeneralHistory;

	// from camera
	camRecordingPeriods: {start: number, end: number}[];
	CamRecordingPeriods_Adjusted: (videoLatencyInS: number)=>{start: number, end: number}[];

	// from muse headband (I think)
	eegSampleFiles: {
		left: EEGSampleFile;
		right: EEGSampleFile;
		sleepStages: number[]; // sleep-stage values, for each ? of the session
	};
	gyroSampleFiles: {
		x: GyroSampleFile;
		y: GyroSampleFile;
		z: GyroSampleFile;
	};

	// from respiration-belt
	respirationDataChunks: GDBelt_RawDataChunk[];
}

export const GetSessionDerivativeData = CreateAccessor((sessionID: string)=>{
	const session = GetSession(sessionID);
	const ableToLoadNewDerivativeData = session != null;
	// if we can't yet load in new derivative data, just clear that derivative data
	if (!ableToLoadNewDerivativeData) return null;

	const generalHistory = GetSessionGeneralHistory(session._folderName) as GeneralHistory;
	const camRecordingPeriods = generalHistory.camFilenames.map(name=>{
		// example: 02.32.39-02.33.25 @Triggered(00.16-00.35).webm
		// Note that conversion of this string into a date-value, is wrong for some edge-cases (eg. DST).
		// 	But that's fine; those offset issues are rare, and are not worth the dev-time at this point.
		const periodMatch = name.match(/^(\d\d)\.(\d\d)\.(\d\d)-(\d\d)\.(\d\d)\.(\d\d) /)!;
		let sessionStartTime = moment(session.startTime);
		if (session.localOffsetFromUTC != null) { // behind conditional, in case user deleted the timezone-offset field
			sessionStartTime = sessionStartTime.utcOffset(session.localOffsetFromUTC);
		}
		let startTime = sessionStartTime.clone().set("h", periodMatch[1].ToInt()).set("m", periodMatch[2].ToInt()).set("s", periodMatch[3].ToInt());
		if (!startTime.valueOf().IsBetween(session.startTime, session.endTime + 10000)) startTime = startTime.add(1, "day");
		let endTime = sessionStartTime.clone().set("h", periodMatch[4].ToInt()).set("m", periodMatch[5].ToInt()).set("s", periodMatch[6].ToInt());
		if (!endTime.valueOf().IsBetween(session.startTime, session.endTime + 10000)) endTime = endTime.add(1, "day");
		return {start: startTime.valueOf(), end: endTime.valueOf()};
	});
	const camRecordingPeriods_adjustedArrays = {}; // we need to return the same array each time (for a given video-latency), so store a map
	const CamRecordingPeriods_Adjusted = (videoLatencyInS: number)=>{
		if (camRecordingPeriods_adjustedArrays[videoLatencyInS] == null) {
			camRecordingPeriods_adjustedArrays[videoLatencyInS] = camRecordingPeriods.map(a=>({start: a.start - (videoLatencyInS * 1000), end: a.end - (videoLatencyInS * 1000)}));
		}
		return camRecordingPeriods_adjustedArrays[videoLatencyInS];
	};

	//uiState.eegSampleFiles = GetSessionEEGSamples_LeftAndRightFiles(session._folderName);
	//const eegData = GetSessionEEGData(session._folderName, true);
	const eegData = GetSessionEEGData(session._folderName); // sleep-stage calculation disabled for now, since the model doesn't seem good enough to be useful atm
	function EEGOrGyroArrayToData(array: Float64Array) {
		const result = {samplesBySecond: {} as SamplesBySecond};
		let currentSecond_key: number, currentSecond_samples = [] as number[];
		for (let i = 0; i < array.length + 1; i++) {
			const val = array[i] ?? 1_000_000; // 1-million for fake key at end, to prompt adding of last second's data
			if (val >= 1_000_000) {
				if (i > 0) {
					result.samplesBySecond[currentSecond_key!] = currentSecond_samples;
				}
				currentSecond_key = val;
				currentSecond_samples = [];
			} else {
				currentSecond_samples.push(val);
			}
		}
		return result;
	}
	const eegSampleFiles = {
		left: EEGOrGyroArrayToData(eegData.left) as GyroSampleFile,
		right: EEGOrGyroArrayToData(eegData.right) as EEGSampleFile,
		sleepStages: eegData.sleepStages,
	};

	const gyroSampleFiles_arrays = GetSessionGyroSampleFiles(session._folderName);
	const gyroSampleFiles = {x: EEGOrGyroArrayToData(gyroSampleFiles_arrays.x), y: EEGOrGyroArrayToData(gyroSampleFiles_arrays.y), z: EEGOrGyroArrayToData(gyroSampleFiles_arrays.z)};

	const respirationDataChunks = gdBelt_rawDataDB.GetChunksForSession_MobX(sessionID);

	return new SessionExternals({
		sessionID,
		// from multiple sources
		generalHistory,
		// from camera
		camRecordingPeriods, CamRecordingPeriods_Adjusted,
		// from muse headband (I think)
		eegSampleFiles, gyroSampleFiles,
		// from respiration-belt
		respirationDataChunks,
	});
});